import {
  Account,
  CreativeReportMetadata,
  CreativeSectionConfiguration,
  CreativeType,
  LLMChoices,
} from "@/types";
import { ImageEditHistoryItem } from "../creative-library/types";

export type Status = "success" | "pending" | "partial" | "failed";
export enum CreativeLevels {
  Account = "account",
  Campaign = "campaign",
  Ad = "ad",
}

export type CreativeConfigRequest = {
  account_id: string;
  platform: string;
};

export type CreativeConfigGroup = {
  group_by: string[];
  filter_by: [
    {
      name: string;
      operators: string[];
    },
  ];
  metrics: string[];
  segment: string[];
  order_by: string[];
};

export type CreativeConfigResponse = {
  [key in CreativeLevels]: CreativeConfigGroup;
};

export type CreativeDataRequest = {
  asset_group_configuration_id: string;
  asset_type: keyof CreativeLevels;
  data_type: string;
  asset_ids: string[];
  dates: {
    start_date: string | undefined;
    end_date: string | undefined;
  }[];
  metrics: string[];
  segment?: string;
  order_by?: {
    name: string;
    type: string;
  };
  group_by?: string[];
  filter_by?: {
    name: string;
    operator: string;
    value: string;
  }[];
};

export enum ReportType {
  Dashboard = "dashboard",
  Regular = "regular",
  Fatigue = "fatigue",
}

export type ViewMode =
  | "table_view"
  | "creative_view"
  | "bar_chart_view"
  | "line_chart_view"
  | "creative_share_view";
export interface CreativeReportListItem {
  id?: string;
  title: string;
  icon?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  account?: string;
  is_public?: boolean;
  public_id?: string;
  metadata?: CreativeReportMetadata;
  type?: ReportType;
  last_saved_at?: string;
  last_synced_at?: string;
  sync_status?: Status;
  order?: number;
}
export interface CreativeReport {
  id: string;
  title: string;
  icon?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  account: string;
  sync_status: Status;
  metadata: CreativeReportMetadata;
  sections: CreativeReportSection[];
  type?: ReportType;
  last_synced_at: string;
  order?: number;
}

export interface CreativeReportSection {
  id: string;
  title: string;
  icon: string | null;
  description: string | null;
  configuration: CreativeSectionConfiguration;
  created_at: string;
  updated_at: string;
  insight: string;
  insight_prompt: string;
  last_synced_at: string;
}

export interface CreativeReportSectionData {
  section: CreativeReportSection;
  data: CreativeReportData | null;
}

export type CreativeReportDataRequest = CreativeReportMetadata;

export interface TextBox {
  x: number;
  y: number;
  text: string;
  width: number;
  height: number;
  text_color: [number, number, number]; // RGB array
  text_alignment: "left" | "center" | "right";
  font_characteristics: {
    style: "normal" | "italic";
    weight: "thin" | "light" | "regular" | "semibold" | "bold";
  };
}

export interface GrabTextMetadata {
  text_metadata: {
    text_boxes: TextBox[];
  };
}

export interface Image {
  id: string;
  url: string;
  hash: string;
  width: number;
  height: number;
  permalink_url?: string;
  uncompressed_url?: string;
  // Added to unify media handling in editors/polling
  status?: "success" | "failed" | "pending";
  aspect_ratio?: string;
  progress?: string;
  thumbnail_url?: string;
  creative_type?: "image" | "video" | "mixed";
  edit_configuration?: Record<string, unknown>;
  background_image_edit?: ImageEditHistoryItem;
  metadata?: {
    grab_text_image_id?: string;
    configuration?: {
      image_url: string;
      logo_file_id: string;
      logo_coordinates: {
        x1: number;
        x2: number;
        y1: number;
        y2: number;
      };
    };
  };
  grab_text_metadata?: GrabTextMetadata;
  client_grabbed_image?: Image;
  raw_data?: {
    id: string;
    url: string;
    hash: string;
    name: string;
    permalink_url: string;
    original_width: number;
    original_height: number;
  };
}

interface CallToAction {
  type: string;
  value: {
    link: string;
    link_format: string;
    link_caption: string;
  };
}

export interface Video {
  title?: string;
  message?: string;
  video_id: string;
  thumbnail_url: string;
  call_to_action?: CallToAction;
  permalink_url: string;
  source: string;
  aspect_ratio?: string;
}

export enum Platform {
  Facebook = "Facebook",
  Instagram = "Instagram",
  Tiktok = "Tiktok",
  TikTok = "TikTok",
  Google = "Google",
  LinkedIn = "LinkedIn",
  Linkedin = "Linkedin",
}

export interface Creative {
  images: Image[];
  videos: Video[];
  thumbnail_url: string;
  titles?: string[];
  bodies?: string[];
  call_to_action_titles?: string[];
  landing_pages?: string[];
  account_name?: string;
  account_icon_url?: string;
  fb_post_url?: string;
  ig_post_url?: string;
  tiktok_post_url?: string;
  platform?: Platform;
  start_date?: string;
  configuration?: {
    image_url: string;
    logo_file_id: string;
    logo_coordinates: {
      x1: number;
      x2: number;
      y1: number;
      y2: number;
    };
  };
}

export interface CreativeReportColumn {
  key: string;
  label: string;
  is_group_by?: boolean;
  is_primary?: boolean;
  column_type?:
    | "text"
    | "number"
    | "text_ai_label"
    | "text_ai_description"
    | "compound"
    | "custom"
    | "node_group"
    | "node"
    | "media_data_node_group"
    | "media_data_node";
  creative_type?: CreativeType;
  label_type?: "unique" | "multiple";
  unique_labels?: string[];
  suffix?: string;
  prefix?: string;
  has_ad_details?: boolean;
  compound_column_type?: string;
  compound_key?: string;
  messages?: {
    info?: string;
    warning?: string;
  };
  sub_type?: string;
  data_type?: string;
  children?: string[];
  default_key?: string;
  show_platforms?: boolean;
  platforms?: string[];
  display_formula?: string | null;
  hidden_from_table?: boolean;
  data_source?: string;
  percentage?: number;
  name?: string;
  type?: string;
}

export interface CreativeReportRow {
  ads: CreativeReportAd[];
  ads_previous: CreativeReportAd[];
  creative_type: "image" | "video" | "mixed";
  creative?: Creative;
  creative_thumbnail?: string | null;
  first_ad_metadata?: CreativeReportAd;
  breakdowns?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface CreativeReportAd {
  creative: Creative;
  ad_id: string;
  ad_name?: string;
  creative_type: "image" | "video" | "mixed";
  creative_id: string;
  [key: string]: unknown;
}

export interface CreativeReportData {
  status: Status;
  data?: {
    creative_metric_columns?: Column[];
    rows: CreativeReportRow[];
    columns: CreativeReportColumn[];
    ad_columns: CreativeReportColumn[];
    breakdown_columns?: CreativeReportColumn[];
    total: Omit<CreativeReportRow, "ads">;
    unique_labels?: string[];
    partial_columns?: {
      label_config?: Array<{
        key: string;
        status: string;
        message: string;
      }>;
      description_config?: Array<{
        key: string;
        status: string;
        message: string;
      }>;
    };
    graph_config?: GraphConfig;
    max_metrics_date?: string;
  };
  message?: string;
  message_title?: string;
  sync_details?: {
    pending_count: number;
    total_count: number;
  };
  section?: CreativeReportSection;
  report?: Omit<
    CreativeReportListItem,
    "icon" | "description" | "created_by" | "account" | "metadata"
  >;
  error_type?: "retry" | "redirect";
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

export interface ServerPaginationState {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface AdDetailPagination {
  page: number;
  page_size: number;
  total_ads: number;
  total_pages: number;
}

export interface AdDetailResponse {
  status: "success" | "error";
  ads: CreativeReportAd[];
  ad_columns: CreativeReportColumn[];
  pagination: AdDetailPagination;
  total?: Omit<CreativeReportRow, "ads">;
  message?: string;
}

export interface GraphConfig {
  x_axis: string;
  y_axis: {
    key: string;
    label: string;
  }[];
  type: "line";
}

export interface CreativeReportMediaItem {
  type: "image" | "video" | "mixed";
  url: string;
  uncompressed_url?: string;
  thumbnail?: string;
  ad_name?: string;
  account_name?: string;
}

export interface VideoRetentionDataPoint {
  percentage: number; // Video duration percentage (0, 25, 50, 75, 95, 100)
  retention: number; // Audience retention percentage at this point
}

export type VideoRetentionData = VideoRetentionDataPoint[];

export type ChatDataConfig = Partial<CreativeReportMetadata> & {
  question: string;
  chat_id: string | null;
  report_id?: string;
  file_ids?: string[];
  section_ids?: string[];
  metric_ids?: string[];
  creative_ids?: string[];
  response_quality: LLMChoices;
  chat_conversation_id?: string;
};

export type PendingAction = {
  type: "ACCOUNT_CHANGE" | "REPORT_CHANGE" | "NAVIGATION";
  payload?: Account | CreativeReportListItem | null;
};

export interface SyncAssetsResponse {
  status: Status;
  message: string;
  sync_details: {
    pending: number;
    total: number;
  };
  first_synced_date: string;
  last_synced_date: string;
  data_sync_end_date: string;
  global_ad_count: number;
  estimated_completion?: {
    min: number; // In minutes
    max: number; // In minutes
  };
}

export interface GraphDataPoint {
  x: string;
  [adName: string]:
    | {
        [metricId: string]: number | string;
      }
    | string;
}

export interface DataGraphColumn {
  key: string;
  label: string;
  is_primary?: boolean;
  column_type?: string;
  data_type?: string;
  data_source?: string;
  name?: string;
  sub_type?: string | null;
  type?: string;
  prefix?: string | null;
  suffix?: string | null;
  platforms?: string[];
  show_platforms?: boolean;
  value_key?: string;
}

export interface DataGraphResponse {
  status: Status;
  message: string;
  data: {
    interval_type: string;
    x_axis: string;
    columns?: DataGraphColumn[];
    interval_ranges: Array<{
      date_range: string;
      start_date: string;
      end_date: string;
      label: string;
    }>;
    data_points: GraphDataPoint[];
    data_points_previous?: GraphDataPoint[];
    data_totals?: GraphDataPoint[];
    data_totals_previous?: GraphDataPoint[];
  };
}

export interface FatigueSettings {
  comparison_period: "daily" | "weekly" | "monthly";
  conditions: Array<{
    metric_id: string;
    operator: "increases" | "decreases";
    mid_fatigue: number;
    high_fatigue: number;
  }>;
}

export interface AssignedLabelItem {
  label: string;
  order: number | null;
}

export interface AssignedLabel {
  ad_id: string;
  ad_name: string;
  labels: AssignedLabelItem[];
}

export interface LabelConfig {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface AssignedLabelsResponse {
  assigned_labels: AssignedLabel[];
  unique_labels: string[];
  label_config: LabelConfig;
}

export interface UpdateAssignedLabelRequest {
  ad_id: string;
  labels: Array<{
    label: string;
    order: number | null;
  }>;
}

export interface UpdateAssignedLabelResponse {
  status: "success" | "failed";
  assigned_labels: AssignedLabel[];
  updated_ad_ids: string[];
}

export interface AccountOverviewData {
  overview_metrics_data: {
    data: {
      [key: string]: number;
    };
    columns: Column[];
    creative_metric_columns: Column[];
  };
  ad_distribution: Array<{
    key: string;
    label: string;
    total: number;
    prefix?: string;
    suffix?: string;
    data: Array<{
      key: string;
      label: string;
      value: number | "N/A";
      percent: number;
      prefix?: string;
      suffix?: string;
    }>;
  }>;
  hooks: Array<{
    key: string;
    label: string;
    status: Status;
    data: Array<{
      label: string;
      value: number | "N/A";
      prefix?: string;
      suffix?: string;
      percent?: number;
      info?: string;
    }>;
  }>;
}

export interface CreativeOverviewPrompt {
  id: string;
  title: string;
  prompt: string;
  creative_type: "image" | "video";
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreativeOverviewItem {
  id: string;
  overview_prompt: CreativeOverviewPrompt;
  content: string | null;
  sync_status: "pending" | "success" | "failed";
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreativeOverviewResponse {
  status: "success";
  data: CreativeOverviewItem[];
}

export interface CreativeOverviewRow extends CreativeReportRow {
  creative_name?: string;
  all_time_rank?: number;
  current_rank?: number | "N/A";
  previous_rank?: number;
  rank_change?: number | "__LOADING__";
  total_spends?: number;
  cac?: number;
  achievements?: Array<{
    label: string;
    value: number | "N/A";
  }>;
  overviews?: Array<{
    is_default: boolean;
    label: string;
    value: string;
  }>;
  category?: string;
  category_label?: string;
  account_contributions?: Array<{ [key: string]: number }>;
  [key: string]: unknown; // Allow dynamic properties like UUID keys
}

export interface Column {
  key: string;
  label: string;
  type?: string;
  column_type?: string;
  prefix: string;
  suffix: string;
  is_kpi?: boolean;
  is_primary?: boolean;
}

export interface CreativeOverviewData {
  trends_data: {
    [key: string]: {
      columns: Column[];
      count: number;
      label: string;
      rows: CreativeOverviewRow[];
    };
  };
  creative_metric_columns: Column[];
}

export interface NewlyLaunchedOverviewData {
  newly_launched: {
    count: number;
    label: string;
    rows: CreativeOverviewRow[];
    columns: Column[];
    ad_columns: Column[];
  };
  creative_metric_columns: Column[];
}

export interface AllTimeCreativeLeaderboardData {
  columns: Column[];
  rows: CreativeOverviewRow[];
  total_count: number;
  ad_columns: Column[];
  creative_metric_columns: Column[];
}

export interface MetricData {
  key: string;
  label: string;
  value: number | string;
  prefix: string;
  suffix: string;
  percentChange?: number | "N/A" | "__LOADING__";
  hasDropdown?: boolean;
}

export interface InsightPrompt {
  id: string;
  title: string;
  description: string;
  user_prompt: string;
  created_at: string;
  updated_at: string;
}

export enum InsightType {
  SectionInsights = "section_insights",
  CompetitorInsights = "competitor_insights",
}
